/**
 * Custom hook for managing Super Admin Onboarding data
 * Handles fetching, filtering, and updating onboarding records
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/api/client';
import { 
  SuperAdminOnboardingData, 
  OnboardingTableRow, 
  OnboardingFilters,
  OnboardingPagination,
  OnboardingUpdatePayload,
  calculateOnboardingProgress
} from '@/types/super-admin-onboarding';
import { UserOnboardingData, OnboardingStatus } from '@/types/onboarding';

interface UseSuperAdminOnboardingOptions {
  page?: number;
  limit?: number;
  initialFilters?: Partial<OnboardingFilters>;
  autoRefresh?: boolean;
}

interface UseSuperAdminOnboardingReturn {
  // Data
  onboardingData: OnboardingTableRow[];
  selectedOnboarding: SuperAdminOnboardingData | null;
  
  // State
  loading: boolean;
  error: string | null;
  
  // Pagination
  pagination: OnboardingPagination;
  
  // Filters
  filters: OnboardingFilters;
  setFilters: (filters: Partial<OnboardingFilters>) => void;
  
  // Actions
  refetch: () => Promise<void>;
  fetchOnboardingDetail: (userId: string) => Promise<SuperAdminOnboardingData | null>;
  updateOnboardingField: (payload: OnboardingUpdatePayload) => Promise<void>;
  updateOnboardingStatus: (userId: string, status: OnboardingStatus) => Promise<void>;
  createRestaurant: (userId: string) => Promise<void>;
  
  // Pagination actions
  setPage: (page: number) => void;
  setPageSize: (limit: number) => void;
}

export const useSuperAdminOnboarding = (
  options: UseSuperAdminOnboardingOptions = {}
): UseSuperAdminOnboardingReturn => {
  const {
    page: initialPage = 1,
    limit: initialLimit = 10,
    initialFilters = {},
    autoRefresh = false
  } = options;

  // State management
  const [onboardingData, setOnboardingData] = useState<OnboardingTableRow[]>([]);
  const [selectedOnboarding, setSelectedOnboarding] = useState<SuperAdminOnboardingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<OnboardingPagination>({
    page: initialPage,
    limit: initialLimit,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [filters, setFiltersState] = useState<OnboardingFilters>({
    search: '',
    role: 'new_sign_up,in_progress', // Default to onboarding roles
    organisation_id: undefined,
    status: undefined,
    stage_filter: undefined,
    ...initialFilters
  });

  // Update filters function
  const setFilters = useCallback((newFilters: Partial<OnboardingFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
  }, []);

  // Set page function
  const setPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  // Set page size function
  const setPageSize = useCallback((limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  // Build query for fetching onboarding data
  const buildOnboardingQuery = useCallback(() => {
    let query = supabase
      .from('user_onboarding')
      .select(`
        *,
        profiles!user_onboarding_user_id_fkey (
          name,
          email,
          role,
          created_at,
          organisation_id
        )
      `, { count: 'exact' });

    // Filter by role (focusing on onboarding users)
    // Also filter out users without profiles
    if (filters.role) {
      const roles = filters.role.split(',');
      query = query
        .in('profiles.role', roles)
        .not('profiles', 'is', null);
    } else {
      // Even when no role filter, exclude users without profiles
      query = query.not('profiles', 'is', null);
    }

    // Filter by organisation
    if (filters.organisation_id && filters.organisation_id !== 'all') {
      if (filters.organisation_id === 'none') {
        query = query.is('profiles.organisation_id', null);
      } else {
        query = query.eq('profiles.organisation_id', filters.organisation_id);
      }
    }

    // Filter by onboarding status - since there's no general onboarding_status column,
    // we'll skip this filter for now. Could be implemented by checking specific status columns
    // if (filters.status) {
    //   // Would need to check multiple status columns to determine overall status
    // }

    // Search by user name or email
    if (filters.search) {
      const searchTerm = `%${filters.search}%`;
      query = query.or(`name.ilike.${searchTerm},email.ilike.${searchTerm}`, { foreignTable: 'profiles' });
    }

    return query;
  }, [filters]);

  // Fetch onboarding data
  const fetchOnboardingData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build and execute query in single call
      const { data, error: fetchError, count } = await buildOnboardingQuery()
        .range(
          (pagination.page - 1) * pagination.limit,
          pagination.page * pagination.limit - 1
        )
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Transform data for table display
      const transformedData: OnboardingTableRow[] = (data || []).map(item => {
        const profile = item.profiles;
        const progress = calculateOnboardingProgress(item as SuperAdminOnboardingData);
        
        return {
          id: item.id,
          user_id: item.user_id,
          user_name: profile?.name || 'Unknown',
          user_email: profile?.email || 'Unknown',
          user_role: profile?.role || 'unknown',
          organisation_id: item.organisation_id || profile?.organisation_id,
          restaurant_name: item.restaurant_name,
          organisation_name: item.organisation_name,
          created_at: item.created_at,
          updated_at: item.updated_at,
          onboarding_status: progress.completedStages === progress.totalStages ? OnboardingStatus.COMPLETE : OnboardingStatus.NOT_STARTED,
          completed_stages: progress.completedStages,
          total_stages: progress.totalStages,
          progress_percentage: progress.progressPercentage,
          business_details_status: item.business_details_status,
          stripe_connection_status: item.stripe_connection_status,
          printer_setup_status: item.printer_setup_status,
          dns_status: item.dns_status,
          website_linked_status: item.website_linked_status,
          google_ordering_status: item.google_ordering_status,
          uber_aggregation_status: item.uber_aggregation_status,
          doordash_aggregation_status: item.doordash_aggregation_status
        };
      });

      setOnboardingData(transformedData);
      setPagination(prev => ({
        ...prev,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / prev.limit)
      }));

    } catch (err) {
      console.error('Error fetching onboarding data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      toast({
        title: "Error",
        description: "Failed to load onboarding data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [buildOnboardingQuery, pagination.page, pagination.limit]);

  // Fetch detailed onboarding data for a specific user
  const fetchOnboardingDetail = useCallback(async (userId: string): Promise<SuperAdminOnboardingData | null> => {
    try {
      const { data, error } = await supabase
        .from('user_onboarding')
        .select(`
          *,
          profiles!user_onboarding_user_id_fkey (
            name,
            email,
            role,
            created_at,
            organisation_id,
            organisations (
              name
            )
          )
        `)
        .eq('user_id', userId)
        .single();

      if (error) throw error;

      if (data) {
        const profile = data.profiles;
        const result: SuperAdminOnboardingData = {
          ...data,
          user_name: profile?.name || 'Unknown',
          user_email: profile?.email || 'Unknown',
          user_role: profile?.role || 'unknown',
          user_created_at: profile?.created_at || '',
          organisation_name: data.organisation_name || profile?.organisations?.name
        };
        
        setSelectedOnboarding(result);
        return result;
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching onboarding detail:', err);
      toast({
        title: "Error",
        description: "Failed to load onboarding details",
        variant: "destructive"
      });
      return null;
    }
  }, []);

  // Update a specific onboarding field
  const updateOnboardingField = useCallback(async (payload: OnboardingUpdatePayload) => {
    try {
      const { error } = await supabase
        .from('user_onboarding')
        .update({
          [payload.field]: payload.value,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', payload.user_id);

      if (error) throw error;

      // Refresh the data
      await fetchOnboardingData();
      
      // If we have a selected onboarding, refresh that too
      if (selectedOnboarding?.user_id === payload.user_id) {
        await fetchOnboardingDetail(payload.user_id);
      }

      toast({
        title: "Success",
        description: "Onboarding data updated successfully"
      });

    } catch (err) {
      console.error('Error updating onboarding field:', err);
      toast({
        title: "Error",
        description: "Failed to update onboarding data",
        variant: "destructive"
      });
      throw err;
    }
  }, [fetchOnboardingData, fetchOnboardingDetail, selectedOnboarding?.user_id]);

  // Update onboarding status
  const updateOnboardingStatus = useCallback(async (userId: string, status: OnboardingStatus) => {
    try {
      // Since the table doesn't have a general onboarding_status column,
      // we'll update the timestamp to indicate completion without changing status columns
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      
      // If completing onboarding, add completion timestamp
      if (status === OnboardingStatus.COMPLETE) {
        updateData.onboarding_completed_at = new Date().toISOString();
      }
      
      const { error } = await supabase
        .from('user_onboarding')
        .update(updateData)
        .eq('user_id', userId);

      if (error) throw error;

      // Refresh the data
      await fetchOnboardingData();
      
      // If we have a selected onboarding, refresh that too
      if (selectedOnboarding?.user_id === userId) {
        await fetchOnboardingDetail(userId);
      }

      toast({
        title: "Success",
        description: `Onboarding status updated to ${status}`
      });

    } catch (err) {
      console.error('Error updating onboarding status:', err);
      toast({
        title: "Error",
        description: "Failed to update onboarding status",
        variant: "destructive"
      });
      throw err;
    }
  }, [fetchOnboardingData, fetchOnboardingDetail, selectedOnboarding?.user_id]);

  // Create restaurant from onboarding data
  const createRestaurant = useCallback(async (userId: string) => {
    try {
      // First get the complete onboarding data
      const onboardingData = await fetchOnboardingDetail(userId);
      if (!onboardingData) {
        throw new Error('Could not load onboarding data');
      }

      // Validate required fields
      if (!onboardingData.restaurant_name) {
        throw new Error('Restaurant name is required to create restaurant');
      }

      // Build restaurant payload using the same structure as SuperAdminRestaurantsTab
      const restaurantPayload = {
        restaurant_name: onboardingData.restaurant_name,
        organisation_id: onboardingData.organisation_id || '',
        organisation_name: onboardingData.organisation_name || '',
        template_restaurant_name: onboardingData.template_restaurant_name ||
                                 onboardingData.organisation_name ||
                                 '',
        restaurant_id: onboardingData.restaurant_id || '',
        address: onboardingData.address || '',
        email: onboardingData.email || '',
        phone: onboardingData.phone || '',
        status: 'active',
        contact_person: onboardingData.contact_person || '',
        
        // Internal fields that aren't in normal onboarding but are required for restaurant creation
        logo_url: onboardingData.logo_url || '',
        webhook_secret: onboardingData.webhook_secret || '',
        // NOTE: Database field 'restaurant_api_key' is mapped to 'api_key' for restaurant creation API compatibility
        api_key: onboardingData.restaurant_api_key || '',
        google_oauth_client_id: onboardingData.google_oauth_client_id || '',
        commission_rate: onboardingData.commission_rate || '',
        ubereats_commission_rate: onboardingData.ubereats_commission_rate || '',
        ubereats_markup: onboardingData.ubereats_markup || '',
        pickup_commission_rate: onboardingData.pickup_commission_rate || '',
        primary_color: onboardingData.primary_color || '#4093ff',
        secondary_color: onboardingData.secondary_color || '',
        stripe_customer_id: onboardingData.stripe_customer_id || '',
        opt_in_checkbox_id: onboardingData.opt_in_checkbox_id || '',
        implicit_consent_enabled: onboardingData.implicit_consent_enabled || false,
        
        // URL fields
        ordering_page_url: onboardingData.ordering_page_url || '',
        review_page_url: onboardingData.review_page_url || '',
        google_review_url: onboardingData.google_review_url || '',
        facebook_url: onboardingData.facebook_url || '',
        instagram_url: onboardingData.instagram_url || ''
      };

      // Parse numeric fields
      if (restaurantPayload.commission_rate) {
        restaurantPayload.commission_rate = parseFloat(restaurantPayload.commission_rate as string);
      }
      if (restaurantPayload.ubereats_commission_rate) {
        restaurantPayload.ubereats_commission_rate = parseFloat(restaurantPayload.ubereats_commission_rate as string);
      }
      if (restaurantPayload.ubereats_markup) {
        restaurantPayload.ubereats_markup = parseFloat(restaurantPayload.ubereats_markup as string);
      }
      if (restaurantPayload.pickup_commission_rate) {
        restaurantPayload.pickup_commission_rate = parseFloat(restaurantPayload.pickup_commission_rate as string);
      }

      // Call the restaurant creation API using the same endpoint as SuperAdminRestaurantsTab
      const result = await apiClient.post('/api/admin/restaurants', restaurantPayload);

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to create restaurant');
      }

      // Update user role to 'rest_admin' after successful restaurant creation
      const { error: roleError } = await supabase
        .from('profiles')
        .update({ role: 'rest_admin' })
        .eq('id', userId);

      if (roleError) {
        console.error('Error updating user role:', roleError);
        // Don't throw here as the restaurant was created successfully
      }

      // Update onboarding status to complete
      await updateOnboardingStatus(userId, OnboardingStatus.COMPLETE);

      toast({
        title: "Success",
        description: "Restaurant created successfully and onboarding completed"
      });

      return result.data;

    } catch (err) {
      console.error('Error creating restaurant:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to create restaurant",
        variant: "destructive"
      });
      throw err;
    }
  }, [updateOnboardingStatus, fetchOnboardingDetail]);

  // Refetch data
  const refetch = useCallback(async () => {
    await fetchOnboardingData();
  }, [fetchOnboardingData]);

  // Initial data fetch and auto-refresh setup
  useEffect(() => {
    fetchOnboardingData();
  }, [fetchOnboardingData]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchOnboardingData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchOnboardingData]);

  return {
    onboardingData,
    selectedOnboarding,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    refetch,
    fetchOnboardingDetail,
    updateOnboardingField,
    updateOnboardingStatus,
    createRestaurant,
    setPage,
    setPageSize
  };
};